<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import WhatsAppCampaignDetailDrawer from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDetailDrawer.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import ConfirmCampaignActionDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmCampaignActionDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const currentAction = ref('pause');
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const confirmActionDialogRef = ref(null);
const drawerRef = ref(null);

const showDrawer = ref(false);
const drawerCampaignId = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const drawerCampaign = computed(
  () =>
    WhatsAppCampaigns.value.find(
      campaign => campaign.id === drawerCampaignId.value
    ) ?? null
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handlePause = campaign => {
  selectedCampaign.value = campaign;
  currentAction.value = 'pause';
  confirmActionDialogRef.value.dialogRef.open();
};

const handleResume = campaign => {
  selectedCampaign.value = campaign;
  currentAction.value = 'resume';
  confirmActionDialogRef.value.dialogRef.open();
};

const handleStop = campaign => {
  selectedCampaign.value = campaign;
  currentAction.value = 'stop';
  confirmActionDialogRef.value.dialogRef.open();
};

const handleCancel = campaign => {
  selectedCampaign.value = campaign;
  currentAction.value = 'cancel';
  confirmActionDialogRef.value.dialogRef.open();
};

const handleViewDetails = campaign => {
  drawerCampaignId.value = campaign.id;
  showDrawer.value = true;
};

const handleDrawerClose = () => {
  showDrawer.value = false;
  drawerCampaignId.value = null;
};

const handleActionComplete = ({ id }) => {
  if (showDrawer.value && drawerCampaignId.value === id) {
    drawerRef.value?.refresh();
  }
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="toggleWhatsAppCampaignDialog()"
    @close="toggleWhatsAppCampaignDialog(false)"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        @close="toggleWhatsAppCampaignDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      @delete="handleDelete"
      @pause="handlePause"
      @resume="handleResume"
      @stop="handleStop"
      @cancel="handleCancel"
      @view-details="handleViewDetails"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmCampaignActionDialog
      ref="confirmActionDialogRef"
      :selected-campaign="selectedCampaign"
      :action="currentAction"
      @action-complete="handleActionComplete"
    />
    <WhatsAppCampaignDetailDrawer
      ref="drawerRef"
      :show="showDrawer"
      :campaign="drawerCampaign"
      @close="handleDrawerClose"
      @pause="handlePause"
      @resume="handleResume"
      @stop="handleStop"
      @cancel="handleCancel"
      @delete="handleDelete"
    />
  </CampaignLayout>
</template>
